import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Sidebar } from 'primereact/sidebar';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import WallTabs from './components/WallTabs';
import { useIsRestaurant } from '../../../hooks/useAccountType';
import WallPreviewRenderer from './preview/WallPreviewRenderer';
import WallVideoSplitPreview from './preview/WallVideoSplitPreview';
import { buildTemplatePreviewPayload } from './preview/buildTemplatePreviewPayload';
import {
  useGetTemplates, useCreateTemplate, useDeleteTemplate, useGetThemes, useQuickSpecial,
} from '../../../queries/signageTemplates';
import { useGetMenus } from '../../../queries/signageMenus';
import { useCreatePlaylist } from '../../../queries/wallPlaylistLibrary';
import { useGetWallMediaLibrary } from '../../../queries/videoWall';

const TYPES = ['menu', 'special'];
const CURRENCIES = ['SAR', 'AED', 'KWD', 'BHD', 'QAR', 'OMR', 'JOD', 'EGP', 'IQD', 'LBP', 'USD', 'EUR'];

/**
 * Smart Template Engine — template gallery. Create menu templates, 30-second
 * Daily Specials (Phase 4), full-wall Promo Videos (Phase 5), and reach Happy
 * Hour (Phase 7). (.claude/TEMPLATE_ENGINE_SPEC.md)
 */
export default function VideoWallTemplates() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRestaurant = useIsRestaurant();

  const { data: templates = [], isLoading } = useGetTemplates();
  const { data: menus = [] } = useGetMenus();
  const { data: themeData } = useGetThemes();
  const themes = themeData?.themes ?? [];
  const defaultZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);
  const create = useCreateTemplate();
  const del = useDeleteTemplate();
  const quickSpecial = useQuickSpecial();
  const createPlaylist = useCreatePlaylist();
  const { data: mediaRaw } = useGetWallMediaLibrary(true);

  const mediaList = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [mediaRaw]);
  const mediaMap = useMemo(() => {
    const m = {}; mediaList.forEach((x) => { m[x.id] = x; }); return m;
  }, [mediaList]);
  const mediaThumb = (id) => mediaMap[id]?.thumb || mediaMap[id]?.path || null;

  // create-template dialog
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('menu');
  const [themeKey, setThemeKey] = useState('noir_gold');
  const [menuId, setMenuId] = useState(null);

  // quick-special dialog
  const [special, setSpecial] = useState(null); // null | { ... }
  // media picker sidebar: { mode: 'special_photo' | 'promo' }
  const [picker, setPicker] = useState(null);
  // promo split preview (1C): { media, rows, cols }
  const [promo, setPromo] = useState(null);

  // Live preview payload for the Daily Special dialog (1B) — a single "Today's
  // Special" section with one item, rendered by the same keystone as everything else.
  const specialPreview = useMemo(() => {
    if (!special) return null;
    return buildTemplatePreviewPayload({
      instance: { type: 'special', theme_key: special.theme_key, settings: {} },
      sections: [{
        id: 'special', name_ar: 'عرض اليوم', name_en: "Today's Special",
        items: [{
          id: 'item',
          name_ar: special.title_ar || null,
          name_en: special.title_en || null,
          price: special.price || null,
          photo_url: special.photo_media_id ? mediaThumb(special.photo_media_id) : null,
          badge_en: 'TODAY',
        }],
      }],
      theme: themes.find((th) => th.key === special.theme_key) || {},
      defaultZones,
      currency: special.currency || 'SAR',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [special, themes, defaultZones]);

  const openEditor = (id) => navigate(`/apps/video-wall/templates/${id}`);
  const openCreate = () => { setName(''); setType('menu'); setThemeKey('noir_gold'); setMenuId(null); setCreating(true); };
  const openSpecial = () => setSpecial({ title_en: '', title_ar: '', price: '', currency: 'SAR', theme_key: 'bold_appetite', photo_media_id: null });

  const saveTemplate = async () => {
    if (!name.trim()) return;
    const res = await create.mutateAsync({ name: name.trim(), type, theme_key: themeKey, menu_id: menuId });
    const body = res?.data?.data ?? res?.data;
    setCreating(false);
    if (body?.template?.id) openEditor(body.template.id);
  };

  const saveSpecial = async () => {
    const s = special;
    if (!s || (!s.title_en.trim() && !s.title_ar.trim())) return;
    const res = await quickSpecial.mutateAsync({
      title_en: s.title_en.trim() || null,
      title_ar: s.title_ar.trim() || null,
      price: s.price.trim() || null,
      currency: s.currency,
      theme_key: s.theme_key,
      photo_media_id: s.photo_media_id,
    });
    const body = res?.data?.data ?? res?.data;
    setSpecial(null);
    if (body?.template?.id) openEditor(body.template.id);
  };

  const pickMedia = (m) => {
    const mode = picker?.mode;
    setPicker(null);
    if (mode === 'special_photo') {
      setSpecial((s) => (s ? { ...s, photo_media_id: m.id } : s));
    } else if (mode === 'promo') {
      // 1C: show the split-across-grid preview first; create on confirm.
      setPromo({ media: m, rows: 2, cols: 2 });
    }
  };

  const createPromo = async () => {
    if (!promo) return;
    const res = await createPlaylist.mutateAsync({
      name: `${promo.media.file_name || 'Promo'} — Promo`,
      items: [{ content_type: 'full_wall', media_file_id: promo.media.id, duration_seconds: 30, fit_mode: 'crop' }],
    });
    const body = res?.data?.data ?? res?.data;
    setPromo(null);
    if (body?.playlist?.id) navigate(`/apps/video-wall/library/${body.playlist.id}`);
  };

  const confirmDelete = (tpl) => confirmDialog({
    message: t('video_wall.templates.delete_confirm', { name: tpl.name }),
    header: t('video_wall.templates.delete_title'),
    icon: 'pi pi-exclamation-triangle',
    acceptClassName: 'p-button-danger',
    accept: () => del.mutate(tpl.id),
  });

  const themeOf = (key) => themes.find((th) => th.key === key);

  return (
    <Layout>
      <ConfirmDialog />
      <WallTabs />
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          {!isRestaurant && (
          <Button text icon="pi pi-arrow-left" label={t('video_wall.library.back_to_walls')}
            onClick={() => navigate('/apps/video-wall/walls')} className="mb-2 p-0" />
          )}
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="pi pi-palette me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('video_wall.templates.title')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.templates.subtitle')}</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button label={t('video_wall.templates.quick_special')} icon="pi pi-bolt" outlined onClick={openSpecial} />
          <Button label={t('video_wall.templates.promo_video')} icon="pi pi-video" outlined
            onClick={() => setPicker({ mode: 'promo' })} />
          <Button label={t('video_wall.templates.create')} icon="pi pi-plus" onClick={openCreate} />
        </div>
      </div>

      {/* Happy Hour cross-link (Phase 7) */}
      <div className="pp-card p-3 mb-4 d-flex align-items-center gap-3 flex-wrap"
        style={{ borderInlineStart: '4px solid var(--pp-primary)' }}>
        <i className="pi pi-clock" style={{ fontSize: 22, color: 'var(--pp-primary)' }} />
        <div className="flex-grow-1">
          <strong style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.templates.happy_hour_title')}</strong>
          <div className="small text-muted">{t('video_wall.templates.happy_hour_hint')}</div>
        </div>
        <Button label={t('video_wall.templates.open_happy_hour')} icon="pi pi-arrow-right" text
          onClick={() => navigate('/apps/happy-hour')} />
      </div>

      {isLoading ? (
        <div className="row g-3">{[1, 2, 3].map((i) => (
          <div key={i} className="col-12 col-md-6 col-xl-4"><Skeleton height="8rem" /></div>
        ))}</div>
      ) : templates.length === 0 ? (
        <div className="pp-card p-5 text-center">
          <span className="pp-empty-icon mb-3" style={{ width: 56, height: 56, fontSize: '1.5rem' }}><i className="pi pi-palette" /></span>
          <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.templates.empty_title')}</h5>
          <p className="text-muted">{t('video_wall.templates.empty_subtitle')}</p>
          <Button label={t('video_wall.templates.create')} icon="pi pi-plus" onClick={openCreate} />
        </div>
      ) : (
        <div className="row g-3">
          {templates.map((tpl) => {
            const th = themeOf(tpl.theme_key);
            const grad = th?.gradient || ['#1A1A1A', '#000000'];
            return (
              <div key={tpl.id} className="col-12 col-md-6 col-xl-4">
                <div className="pp-card pp-card-clickable h-100 d-flex flex-column" style={{ overflow: 'hidden' }}
                  role="button" tabIndex={0}
                  onClick={() => openEditor(tpl.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditor(tpl.id); } }}>
                  <div style={{ height: 72, background: `linear-gradient(to bottom, ${grad[0]}, ${grad[grad.length - 1]})` }} />
                  <div className="p-3 d-flex flex-column flex-grow-1">
                    <h6 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>{tpl.name}</h6>
                    <span className="badge align-self-start" style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)' }}>
                      {t(`video_wall.templates.type_${tpl.type}`)} · {th?.label || tpl.theme_key}
                    </span>
                    <div className="pp-card-actions mt-auto pt-3">
                      <Button size="small" icon="pi pi-pencil" label={t('video_wall.menus.edit')}
                        onClick={(e) => { e.stopPropagation(); openEditor(tpl.id); }} />
                      <Button className="pp-card-del" text rounded severity="danger" size="small" icon="pi pi-trash"
                        onClick={(e) => { e.stopPropagation(); confirmDelete(tpl); }} aria-label={t('video_wall.menus.delete')} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create template dialog */}
      <Dialog visible={creating} onHide={() => setCreating(false)} header={t('video_wall.templates.create_title')}
        style={{ width: 'min(94vw, 480px)' }}
        footer={(
          <div className="d-flex justify-content-end gap-2">
            <Button label={t('video_wall.library.cancel')} text onClick={() => setCreating(false)} />
            <Button label={t('video_wall.library.save')} icon="pi pi-check" onClick={saveTemplate}
              loading={create.isLoading} disabled={!name.trim()} />
          </div>
        )}>
        <div className="d-flex flex-column gap-3 pt-2">
          <Field label={t('video_wall.templates.name')}>
            <InputText className="w-100" value={name} onChange={(e) => setName(e.target.value)} maxLength={150} autoFocus />
          </Field>
          <Field label={t('video_wall.templates.type')}>
            <Dropdown className="w-100" value={type} onChange={(e) => setType(e.value)}
              options={TYPES.map((c) => ({ label: t(`video_wall.templates.type_${c}`), value: c }))} />
          </Field>
          <Field label={t('video_wall.templates.theme')}>
            <Dropdown className="w-100" value={themeKey} onChange={(e) => setThemeKey(e.value)}
              options={themes.map((th) => ({ label: th.label, value: th.key }))} />
          </Field>
          <Field label={t('video_wall.templates.menu')}>
            <Dropdown className="w-100" value={menuId} onChange={(e) => setMenuId(e.value)} showClear filter
              options={menus.map((m) => ({ label: m.name, value: m.id }))} placeholder={t('video_wall.templates.pick_menu')} />
          </Field>
        </div>
      </Dialog>

      {/* Quick Special dialog (Phase 4) */}
      <Dialog visible={!!special} onHide={() => setSpecial(null)} header={t('video_wall.templates.special_title')}
        style={{ width: 'min(94vw, 480px)' }}
        footer={special && (
          <div className="d-flex justify-content-end gap-2">
            <Button label={t('video_wall.library.cancel')} text onClick={() => setSpecial(null)} />
            <Button label={t('video_wall.templates.special_save')} icon="pi pi-check" onClick={saveSpecial}
              loading={quickSpecial.isLoading} disabled={!special.title_en?.trim() && !special.title_ar?.trim()} />
          </div>
        )}>
        {special && (
          <div className="d-flex flex-column gap-3 pt-2">
            <p className="small text-muted mb-0">{t('video_wall.templates.special_hint')}</p>
            {specialPreview && (
              <div className="d-flex justify-content-center">
                <WallPreviewRenderer template={specialPreview} grid={{ rows: 1, cols: 1, orientation: 'landscape' }} maxWidth={360} />
              </div>
            )}
            <Field label={t('video_wall.templates.special_ar')}>
              <InputText className="w-100" dir="rtl" value={special.title_ar}
                onChange={(e) => setSpecial({ ...special, title_ar: e.target.value })} placeholder="منسف" />
            </Field>
            <Field label={t('video_wall.templates.special_en')}>
              <InputText className="w-100" value={special.title_en}
                onChange={(e) => setSpecial({ ...special, title_en: e.target.value })} placeholder="Mansaf" />
            </Field>
            <div className="d-flex gap-3">
              <Field label={t('video_wall.menus.price')} className="flex-grow-1">
                <InputText className="w-100" value={special.price}
                  onChange={(e) => setSpecial({ ...special, price: e.target.value })} placeholder="25" />
              </Field>
              <Field label={t('video_wall.menus.currency')}>
                <Dropdown value={special.currency} options={CURRENCIES.map((c) => ({ label: c, value: c }))}
                  onChange={(e) => setSpecial({ ...special, currency: e.value })} />
              </Field>
            </div>
            <Field label={t('video_wall.templates.theme')}>
              <Dropdown className="w-100" value={special.theme_key}
                options={themes.map((th) => ({ label: th.label, value: th.key }))}
                onChange={(e) => setSpecial({ ...special, theme_key: e.value })} />
            </Field>
            <div>
              <Button outlined size="small" icon="pi pi-image" label={t('video_wall.menus.photo')}
                onClick={() => setPicker({ mode: 'special_photo' })} />
              {special.photo_media_id && mediaThumb(special.photo_media_id) && (
                <img src={mediaThumb(special.photo_media_id)} alt="" className="ms-2"
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, verticalAlign: 'middle' }} />
              )}
            </div>
          </div>
        )}
      </Dialog>

      {/* Promo split-across-grid preview (1C) */}
      <Dialog visible={!!promo} onHide={() => setPromo(null)} header={t('video_wall.templates.promo_title')}
        style={{ width: 'min(96vw, 640px)' }}
        footer={promo && (
          <div className="d-flex justify-content-end gap-2">
            <Button label={t('video_wall.library.cancel')} text onClick={() => setPromo(null)} />
            <Button label={t('video_wall.templates.promo_create')} icon="pi pi-check" onClick={createPromo}
              loading={createPlaylist.isLoading} />
          </div>
        )}>
        {promo && (
          <div className="d-flex flex-column align-items-center gap-3 pt-2">
            <p className="small text-muted mb-0 text-center">{t('video_wall.templates.promo_split_hint')}</p>
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {[[1, 1], [1, 2], [2, 2], [1, 3]].map(([r, c]) => (
                <Button key={`${r}x${c}`} size="small" label={`${r} × ${c}`}
                  outlined={!(promo.rows === r && promo.cols === c)}
                  onClick={() => setPromo({ ...promo, rows: r, cols: c })} />
              ))}
            </div>
            <WallVideoSplitPreview src={promo.media.path} grid={{ rows: promo.rows, cols: promo.cols, orientation: 'landscape' }} maxWidth={520} />
          </div>
        )}
      </Dialog>

      {/* Shared media picker (special photo + promo video) */}
      <Sidebar visible={!!picker} position="right" onHide={() => setPicker(null)} style={{ width: 'min(96vw, 440px)' }}>
        <h5 className="mb-3" style={{ color: 'var(--pp-text-primary)' }}>
          {picker?.mode === 'promo' ? t('video_wall.templates.pick_promo') : t('video_wall.menus.pick_photo')}
        </h5>
        {mediaList.length === 0 ? (
          <p className="text-muted">{t('video_wall.menus.no_media')}</p>
        ) : (
          <div className="row g-2">
            {mediaList.map((m) => (
              <div key={m.id} className="col-6">
                <div role="button" onClick={() => pickMedia(m)} className="pp-card p-1" style={{ cursor: 'pointer' }}>
                  <img src={m.thumb || m.path} alt={m.file_name}
                    style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 6 }} />
                  <div className="small text-truncate px-1" style={{ color: 'var(--pp-text-secondary)' }}>{m.file_name}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Sidebar>
    </Layout>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{label}</label>
      {children}
    </div>
  );
}
